import Skeleton from "react-loading-skeleton";
import { Box } from "../Box/Box";
import { Hidden } from "../Hidden/Hidden";
import { Text } from "../Text/Text";
import type { Color } from "../utils/colors";
import styles from "./HotDealCard.module.css";

export const HOT_DEAL_CARD_HEIGHT = 150;

export interface HotDealCardProps {
  title: string;
  description?: string | null;
  disabled?: boolean;
  onSelect(): void;
  tabIndex?: number;
  // Overrides the default green border colour.
  borderColor?: Color;
}

export function HotDealCard(props: HotDealCardProps): React.ReactNode {
  const {
    title,
    description,
    disabled = false,
    onSelect,
    tabIndex = 0,
    borderColor = "primary",
  } = props;

  // priceDescription: e.g. $5 Off, $25.99!, etc.
  const [bodyText, priceDescription] = disabled
    ? ["Currently unavailable"]
    : (description || "").trim().split("\n");

  return (
    <Box
      border="dashed"
      borderColor={disabled ? "primary-highlight" : borderColor}
      borderRadius="standard"
    >
      <Box
        background={disabled ? "neutral-700" : "jets-black"}
        borderRadius="standard"
        display="flex"
        onClick={() => !disabled && onSelect()}
        disabled={disabled}
        flexDirection="column"
        tabIndex={disabled ? undefined : tabIndex}
        role="button"
        aria-disabled={disabled}
        aria-label={`Hot Deal: ${title}`}
      >
        <Hidden above="mobile">
          <HotDealCardBody
            height={HOT_DEAL_CARD_HEIGHT}
            lines={priceDescription ? "3" : "5"}
            title={title}
            description={bodyText}
            priceDescription={priceDescription}
            disabled={disabled}
          />
        </Hidden>
        <Hidden below="tablet">
          <Hidden above="tablet">
            <HotDealCardBody
              height={HOT_DEAL_CARD_HEIGHT - 5}
              lines={priceDescription ? "3" : "5"}
              title={title}
              description={bodyText}
              priceDescription={priceDescription}
              disabled={disabled}
            />
          </Hidden>
        </Hidden>
        <Hidden below="desktop">
          <HotDealCardBody
            height={HOT_DEAL_CARD_HEIGHT - 10}
            lines={priceDescription ? "3" : "5"}
            title={title}
            description={bodyText}
            priceDescription={priceDescription}
            disabled={disabled}
          />
        </Hidden>
      </Box>
    </Box>
  );
}

function HotDealCardBody(props: {
  height: number;
  truncateTitle?: boolean;
  title: string;
  description: string;
  lines: "2" | "3" | "4" | "5";
  priceDescription: string;
  disabled?: boolean;
}) {
  const textColor: Color = props.disabled ? "neutral-400" : "overPrimary";

  return (
    <div
      style={{
        height: props.height,
      }}
      className={styles.hotDealCardBody}
    >
      <div>
        <Text
          size="large"
          weight="strong"
          font="accent"
          transform="uppercase"
          truncate={props.truncateTitle}
          clamped={props.truncateTitle ? undefined : "2"}
          color={textColor}
        >
          {props.title}
        </Text>
      </div>
      <div
        style={{
          display: "flex",
          flex: 1,
        }}
      >
        <div>
          <Text clamped={props.lines} priority="low" color={textColor}>
            {props.description}
          </Text>
        </div>
      </div>
      {props.priceDescription && (
        <div style={{ flex: 0 }}>
          <Text
            color={props.disabled ? "neutral-400" : "jets-red"}
            size="xxlarge"
            weight="strong"
            font="accent"
            transform="uppercase"
            truncate
          >
            {props.priceDescription}
          </Text>
        </div>
      )}
    </div>
  );
}

export function HotDealCardSkeleton() {
  return (
    <div style={{ borderRadius: 6 }} aria-hidden="true">
      <Skeleton width={"100%"} height={120} />
    </div>
  );
}
